import InstructionDecoder from '../../src/InstructionDecoder';
import type { ModuleInfo } from '../../src/BinaryReader';
import { flattenTypes } from './wasm-types';

// The target_features custom section uses the WASM proposal names; map them to the
// internal opcode-feature names so a feature isn't listed twice (e.g. the toolchain's
// "nontrapping-fptoint" and the opcode scan's "sat-trunc" are the same feature).
const TARGET_FEATURE_ALIASES: Record<string, string> = {
  'nontrapping-fptoint': 'sat-trunc',
  'sign-ext': 'sign-extend',
  'multivalue': 'multi-value',
};

// Features not carried by a function-body opcode. Without a note they would show a
// bare "used" badge with nothing to click, reading as "nothing uses this".
const MODULE_LEVEL_FEATURE_NOTES: Record<string, string> = {
  'mutable-globals': 'Module-level: one or more globals are mutable',
  'extended-const': 'Constant expressions in global / data-segment offsets',
  'reference-types': 'Reference values and funcref/externref tables',
  'bulk-memory-opt': 'Optimized bulk-memory instruction encoding',
  'call-indirect-overlong': 'Overlong LEB encoding of the call_indirect index',
};

/** One reconciled feature card: usage from opcodes, structure, and target_features merged by name. */
export interface FeatureSummary {
  name: string;
  opcodes: string[];
  functionIndices: number[];
  targetStatus: string | null;
  structuralDetail: string | null;
  fallbackNote: string | null;
}

export interface FeatureAnalysis {
  specLevel: string;
  opcodeFeatureCount: number;
  structuralCount: number;
  targetFeatureCount: number;
  summaries: FeatureSummary[];
}

/**
 * Reconciles the three feature sources — opcode scan (per-function usage + opcodes),
 * structural checks, and the target_features custom section — into one card per
 * feature, keyed by a single canonical name so the same feature is never listed twice.
 */
export function analyzeFeatures(moduleInfo: ModuleInfo, importedFuncCount: number): FeatureAnalysis {
  const featureOpcodes = new Map<string, string[]>();
  const featureFunctions = new Map<string, Set<number>>();
  scanOpcodeFeatures(moduleInfo, importedFuncCount, featureOpcodes, featureFunctions);
  addMultiValueFunctions(moduleInfo, importedFuncCount, featureFunctions);

  const structuralDetailByName = computeStructuralDetails(moduleInfo);
  const targetFeatures = parseTargetFeatures(moduleInfo);
  const allNames = new Set<string>([...featureOpcodes.keys(), ...structuralDetailByName.keys(), ...targetFeatures.keys()]);

  const summaries: FeatureSummary[] = [];
  for (const name of [...allNames].sort()) {
    const opcodes = featureOpcodes.get(name) ?? [];
    const functionIndices = [...(featureFunctions.get(name) ?? [])].sort((first, second) => first - second);
    const structuralDetail = structuralDetailByName.get(name) ?? null;
    const hasUsage = functionIndices.length > 0 || opcodes.length > 0 || structuralDetail !== null;
    summaries.push({
      name,
      opcodes,
      functionIndices,
      targetStatus: targetFeatures.get(name) ?? null,
      structuralDetail,
      fallbackNote: hasUsage ? null : (MODULE_LEVEL_FEATURE_NOTES[name] ?? 'Declared in target_features; no in-body usage'),
    });
  }

  return {
    specLevel: computeSpecLevel(allNames),
    opcodeFeatureCount: featureOpcodes.size,
    structuralCount: structuralDetailByName.size,
    targetFeatureCount: targetFeatures.size,
    summaries,
  };
}

function scanOpcodeFeatures(
  moduleInfo: ModuleInfo,
  importedFuncCount: number,
  featureOpcodes: Map<string, string[]>,
  featureFunctions: Map<string, Set<number>>,
): void {
  for (let funcIndex = 0; funcIndex < moduleInfo.functions.length; funcIndex++) {
    const func = moduleInfo.functions[funcIndex];
    const instructions = func.instructions || InstructionDecoder.decodeFunctionBody(func.body);
    const globalIndex = importedFuncCount + funcIndex;
    for (const instruction of instructions) {
      const feature = instruction.opCode.feature;
      if (!feature) {
        continue;
      }
      if (!featureOpcodes.has(feature)) { featureOpcodes.set(feature, []); }
      const opcodes = featureOpcodes.get(feature)!;
      if (!opcodes.includes(instruction.opCode.mnemonic)) { opcodes.push(instruction.opCode.mnemonic); }
      if (!featureFunctions.has(feature)) { featureFunctions.set(feature, new Set()); }
      featureFunctions.get(feature)!.add(globalIndex);
    }
  }
}

// Multi-value is a type-level feature; link the functions whose signature returns
// more than one value so its card has clickable usage like the opcode features.
function addMultiValueFunctions(moduleInfo: ModuleInfo, importedFuncCount: number, featureFunctions: Map<string, Set<number>>): void {
  const flatTypes = flattenTypes(moduleInfo);
  for (let funcIndex = 0; funcIndex < moduleInfo.functions.length; funcIndex++) {
    const typeEntry = flatTypes[moduleInfo.functions[funcIndex].typeIndex];
    if (typeEntry && typeEntry.kind === 'func' && typeEntry.returnTypes.length > 1) {
      if (!featureFunctions.has('multi-value')) { featureFunctions.set('multi-value', new Set()); }
      featureFunctions.get('multi-value')!.add(importedFuncCount + funcIndex);
    }
  }
}

function computeStructuralDetails(moduleInfo: ModuleInfo): Map<string, string> {
  const checks: { name: string; detected: boolean; detail: string }[] = [
    { name: 'multi-value', detected: moduleInfo.types.some(t => t.kind === 'func' && t.returnTypes.length > 1), detail: 'Functions with multiple return values' },
    { name: 'gc', detected: moduleInfo.types.some(t => t.kind === 'struct' || t.kind === 'array' || t.kind === 'rec'), detail: 'Struct/array/rec types' },
    { name: 'shared-memory', detected: moduleInfo.memories.some(m => m.shared) || moduleInfo.imports.some(i => i.memoryType?.shared), detail: 'Shared linear memory' },
    { name: 'memory64', detected: moduleInfo.memories.some(m => m.memory64) || moduleInfo.imports.some(i => i.memoryType?.memory64), detail: '64-bit memory addressing' },
    { name: 'exception-handling', detected: moduleInfo.tags.length > 0 || moduleInfo.imports.some(i => i.kind === 4), detail: 'Tags for exception handling' },
  ];
  const detailByName = new Map<string, string>();
  for (const check of checks) {
    if (check.detected) { detailByName.set(check.name, check.detail); }
  }
  return detailByName;
}

function parseTargetFeatures(moduleInfo: ModuleInfo): Map<string, string> {
  const targetFeatures = new Map<string, string>();
  const section = moduleInfo.customSections.find(entry => entry.name === 'target_features');
  if (!section) {
    return targetFeatures;
  }
  try {
    const data = section.data;
    const decoder = new TextDecoder();
    let offset = 0;
    const readULEB = (): number => { let result = 0, shift = 0, byte: number; do { byte = data[offset++]; result |= (byte & 0x7f) << shift; shift += 7; } while (byte & 0x80); return result >>> 0; };
    const readStr = (): string => { const length = readULEB(); const value = decoder.decode(data.slice(offset, offset + length)); offset += length; return value; };
    const prefixLabels: Record<number, string> = { 0x2b: 'used', 0x2d: 'disallowed', 0x3d: 'required' };
    const count = readULEB();
    for (let index = 0; index < count; index++) {
      const prefix = data[offset++];
      const name = readStr();
      targetFeatures.set(TARGET_FEATURE_ALIASES[name] || name, prefixLabels[prefix] || 'unknown');
    }
  } catch {
    // ignore parse failures
  }
  return targetFeatures;
}

function computeSpecLevel(featureNames: Set<string>): string {
  const hasGc = featureNames.has('gc') || featureNames.has('function-references') || featureNames.has('reference-types');
  const hasThreads = featureNames.has('atomics') || featureNames.has('shared-memory') || featureNames.has('threads');
  const hasSimd = featureNames.has('simd128') || featureNames.has('simd') || featureNames.has('relaxed-simd');
  const hasExceptions = featureNames.has('exception-handling');
  if (hasGc) {
    return 'Wasm 3.0 (GC)';
  }
  if (hasThreads || hasSimd || hasExceptions) {
    return 'Wasm 2.0+';
  }
  return featureNames.size > 0 ? 'Wasm 2.0' : 'MVP (1.0)';
}
